/**
 * useUnifiedApi Hook
 * 
 * This React hook provides easy access to the unified API service.
 * Your components can use this hook and never worry about which
 * backend system they're talking to.
 * 
 * Usage:
 * const api = useUnifiedApi();
 * const tag = await api.scanTag('RFID123456');
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createUnifiedApiService, UnifiedApiService } from '../services/unifiedApiService';

// Global cache to prevent multiple API service instances
const apiServiceCache = new Map<string, UnifiedApiService>();

// Clear cache (useful for logout)
export const clearApiServiceCache = () => {
  console.log('🧹 Clearing API service cache');
  apiServiceCache.clear();
};

export const useUnifiedApi = (): UnifiedApiService | null => {
  const { user, isAuthenticated } = useAuth();

  // Create the API service instance with caching
  const apiService = useMemo(() => {
    if (!isAuthenticated || !user) {
      return null;
    }

    // Create a cache key based on user credentials
    const cacheKey = `${user.system}-${user.username}-${user.id}`;
    
    // Check if we already have this service instance
    if (apiServiceCache.has(cacheKey)) {
      console.log('♻️ Reusing cached API service for user:', user.username, user.system);
      return apiServiceCache.get(cacheKey)!;
    }

    console.log('🔄 Creating new unified API service for user:', user.username, user.system);
    const service = createUnifiedApiService(user);
    apiServiceCache.set(cacheKey, service);
    return service;
  }, [user?.id, user?.username, user?.system, isAuthenticated]); // More specific dependencies

  return apiService;
};

/**
 * Hook for tag operations specifically
 * Provides commonly used tag functions with error handling
 */
export const useTagOperations = () => {
  const api = useUnifiedApi();

  const scanTag = useCallback(async (rfidNumber: string) => {
    if (!api) throw new Error('Not authenticated');
    return await api.scanTag(rfidNumber);
  }, [api]);

  const createTagMatch = useCallback(async (productId: string, rfidNumber: string, quantity: number = 1) => {
    if (!api) throw new Error('Not authenticated');
    return await api.createTagMatch(productId, rfidNumber, quantity);
  }, [api]);

  const updateTagStatus = useCallback(async (rfidNumber: string, status: any, notes?: string) => {
    if (!api) throw new Error('Not authenticated');
    return await api.updateTagStatus(rfidNumber, status, notes);
  }, [api]);

  return {
    scanTag,
    createTagMatch,
    updateTagStatus,
    isReady: !!api
  };
};

/**
 * Hook for product operations specifically
 */
export const useProductOperations = () => {
  const api = useUnifiedApi();

  const searchProducts = useCallback(async (query: string, options?: { category?: string; limit?: number }) => {
    if (!api) throw new Error('Not authenticated');
    return await api.searchProducts({
      query,
      category: options?.category,
      limit: options?.limit || 20,
      offset: 0
    });
  }, [api]);

  const getProductById = useCallback(async (productId: string) => {
    if (!api) throw new Error('Not authenticated');
    return await api.getProductById(productId);
  }, [api]);

  const getProductCategories = useCallback(async () => {
    if (!api) throw new Error('Not authenticated');
    return await api.getProductCategories();
  }, [api]);

  return {
    searchProducts,
    getProductById,
    getProductCategories,
    isReady: !!api
  };
};

